import ButtonComponent from '@/components/ButtonComponent';
import FormInput from '@/components/FormInput';
import GlobalAlert from '@/components/GlobalAlert';
import TextComponent from '@/components/TextComponent';
import useTimer from '@/hooks/common/useTimer';
import { Contact, ContactService } from '@/services/phamacy/interface';
import styled from '@/styles/theme-components';
import { getErrorMessage } from '@/utils/error';
import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import theme from '@/styles/theme';

type ContactInputs = {
  target: string;
  token: string;
};

interface ContactUpdateModalProps {
  contactList?: ContactService;
  onSend: ({ target }: Pick<ContactInputs, 'target'>) => void;
  onApprove: ({ target, token }: ContactInputs) => void;
  onSubmit: (contacts: ContactService) => void;
  errorContacts: string;
}

const ContactUpdateModal = ({
  contactList,
  onSend,
  onApprove,
  onSubmit,
  errorContacts
}: ContactUpdateModalProps) => {
  const [contacts, setContacts] = useState<ContactService>(contactList || []);

  const onSubmitContacts = () => {
    onSubmit(contacts);
  };

  const onChangeContact = (i: number) => (props: Partial<Contact>) => {
    setContacts(currentContacts =>
      currentContacts.map((contact, index) =>
        i === index
          ? {
              ...contact,
              ...props
            }
          : contact
      )
    );
  };

  return (
    <ContactWrapper>
      {contacts.map((contact, i) => (
        <ContactUpdatePhoneNumber
          {...contact}
          index={i}
          onSend={onSend}
          onApprove={onApprove}
          onChange={onChangeContact(i)}
        />
      ))}
      {errorContacts && <ErrorText>{errorContacts}</ErrorText>}
      <SubmitWrapper>
        <SubmitButton
          type="button"
          bgColor={theme.DF_COLOR.GREEN500}
          borderColor={theme.DF_COLOR.GREEN500}
          textColor={theme.DF_COLOR.WHITE}
          text="수정완료"
          onClick={onSubmitContacts}
        />
      </SubmitWrapper>
    </ContactWrapper>
  );
};

const ContactUpdatePhoneNumber = ({
  contactAlias,
  phone,
  isSaved = false,
  index,
  onSend,
  onApprove,
  onChange
}: Contact & {
  index: number;
  onSend: ({ target }: Pick<ContactInputs, 'target'>) => void;
  onApprove: ({ target, token }: ContactInputs) => void;
  onChange: (props: Partial<Contact>) => void;
}) => {
  const itemRef = useRef<HTMLInputElement>(null);
  const { control, errors, handleSubmit, reset, watch } = useForm<{
    target: string;
    token: string;
  }>({
    defaultValues: {
      target: phone,
      token: ''
    },
    mode: 'onChange'
  });
  // 승인까지 완료된 상태인지
  const [saved, setSaved] = useState(isSaved);
  // 승인문자를 보냈는지
  const [sentToken, setSentToken] = useState(0);
  // 승인 문자까지 완료
  const [finished, setFinished] = useState(false);
  // 에러 유무
  const [error, setError] = useState(false);
  const { min, sec } = useTimer({
    active: sentToken
  });
  const { target, token } = watch();

  const isSentToken = !!sentToken;
  const isDisabledTarget = !target || !!errors.target;
  const isDisabledToken = !token || token.length < 6 || finished;

  const onToggleTrueTokenSaved = () => {
    setSentToken(sentToken + 1);
  };
  const onToggleTrueSaved = () => {
    setSaved(true);
  };

  const onToggleFinished = () => setFinished(!finished);

  const onToggleSaved = () => {
    setSaved(!saved);
    onChange({
      isActive: false,
      isSaved: false,
      phone: ''
    });
    reset({ target: '', token: '' });
    setSentToken(0);
    setFinished(false);
    setTimeout(() => {
      itemRef.current?.focus();
    });
  };

  const onSendToken = async (props: Pick<ContactInputs, 'target'>) => {
    try {
      await onSend(props);
      onToggleTrueTokenSaved();
    } catch (error) {
      GlobalAlert.show({
        title: getErrorMessage(error)
      });
    }
  };

  const onApproveToken = async (props: ContactInputs) => {
    const { target } = props;
    try {
      await onApprove(props);
      onChange({
        phone: target,
        isActive: true,
        isSaved: true
      });
      setError(false);
      onToggleTrueSaved();
      onToggleFinished();
    } catch (error) {
      setError(true);
      GlobalAlert.show({
        title: getErrorMessage(error)
      });
    }
  };

  return (
    <ContactPhoneNumberWrapper key={contactAlias ?? `Contact-${index}`}>
      <ContactRow>
        <ContactText>{contactAlias}</ContactText>
        <FormInput
          ref={itemRef}
          keyName="target"
          control={control}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange({
              phone: e.target.value
            })
          }
          value={target}
          disabled={saved}
          rules={{
            pattern: /^\d{3}\d{3,4}\d{4}$/
          }}
          width={272}
          style={{
            padding: '14px 16px'
          }}>
          {isSentToken && !finished && (
            <ContactApproveTimer>
              {min}:{sec}
            </ContactApproveTimer>
          )}
        </FormInput>
        {saved ? (
          <ContactButton
            type="button"
            bgColor={theme.DF_COLOR.GRAY600}
            borderColor={theme.DF_COLOR.GRAY600}
            textColor={theme.DF_COLOR.WHITE}
            text={'수정'}
            onClick={onToggleSaved}
          />
        ) : (
          <ContactButton
            bgColor={theme.DF_COLOR.GRAY600}
            borderColor={theme.DF_COLOR.GRAY600}
            textColor={theme.DF_COLOR.WHITE}
            disabled={isDisabledTarget}
            text={isSentToken ? '다시 받기' : '인증번호 받기'}
            onClick={handleSubmit(onSendToken)}
          />
        )}
      </ContactRow>
      {isSentToken && (
        <ContactRow>
          <ContactText />
          <FormInput
            keyName="token"
            control={control}
            rules={{
              pattern: /^\d{6}$/
            }}
            value={token}
            width={272}
            style={{
              padding: '14px 16px'
            }}
            error={
              error
                ? { type: 'validate', message: '인증번호가 올바르지 않습니다.' }
                : undefined
            }>
            {finished && <ApproveSuccess>인증완료</ApproveSuccess>}
          </FormInput>
          <ContactButton
            type="button"
            bgColor={theme.DF_COLOR.GRAY600}
            borderColor={theme.DF_COLOR.GRAY600}
            textColor={theme.DF_COLOR.WHITE}
            text={'인증'}
            disabled={isDisabledToken}
            onClick={handleSubmit(onApproveToken)}
            style={{ alignSelf: 'flex-start' }}
          />
        </ContactRow>
      )}
    </ContactPhoneNumberWrapper>
  );
};

export default ContactUpdateModal;

const ContactWrapper = styled.div``;

const ContactPhoneNumberWrapper = styled.div`
  margin-top: 24px;
`;

const ContactRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 12px;
`;

const ContactText = styled(TextComponent)`
  width: 80px;
  margin-right: 16px;
`;

const ContactButton = styled(ButtonComponent)`
  padding: 14px 0;
  margin-left: 8px;
  width: 120px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 17px;
  font-weight: 500;
`;

const ApproveSuccess = styled.p`
  color: ${theme.DF_COLOR.GREEN500};
  font-size: 15px;
  font-weight: 400;
  line-height: 18px;
  text-align: end;
`;

const ContactApproveTimer = styled(ApproveSuccess)`
  color: ${theme.DF_COLOR.GRAY400};
`;

const SubmitWrapper = styled.div`
  margin-top: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SubmitButton = styled(ButtonComponent)`
  padding: 16px 0;
  width: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  font-weight: 500;
`;

const ErrorText = styled.p`
  color: ${theme.DF_COLOR.RED700};
  font-size: 15px;
  font-weight: 400;
`;
